import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Center,
  Group,
  Loader,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Table,
  Tabs,
  Text,
  Title,
} from "@mantine/core";
import { BarChart } from "@mantine/charts";
import {
  getAllDistributors,
  getDistributorOrgs,
  getVersionDistribution,
} from "@utils/api-communication/RestApiCli";
import { Organization } from "types/orgTypes";
import { aggregateByVersion, DeviceRow, normalizeVersion, toDeviceRows } from "./distributionUtils";

interface DistributorLike {
  distId: string;
  name?: string;
  orgIds?: string[];
}

interface DeviceDetailTableProps {
  version: string;
  devices: DeviceRow[];
  showOrg: boolean;
}

const DeviceDetailTable: React.FC<DeviceDetailTableProps> = ({ version, devices, showOrg }) => (
  <Paper withBorder radius="sm" p="sm">
    <Group gap="xs" mb="xs">
      <Badge variant="light" color="blue">
        {version}
      </Badge>
      <Text size="sm" c="dimmed">
        {devices.length} device{devices.length === 1 ? "" : "s"}
      </Text>
    </Group>
    <ScrollArea.Autosize mah={320}>
      <Table verticalSpacing="xs" horizontalSpacing="sm" striped stickyHeader>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>UUID</Table.Th>
            <Table.Th>Alias</Table.Th>
            {showOrg && <Table.Th>Organization</Table.Th>}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {devices.map((d) => (
            <Table.Tr key={d.uuid}>
              <Table.Td>
                <Text size="xs" style={{ wordBreak: "break-all" }}>
                  {d.uuid}
                </Text>
              </Table.Td>
              <Table.Td>
                <Text size="xs">{d.alias || "-"}</Text>
              </Table.Td>
              {showOrg && (
                <Table.Td>
                  {d.orgId ? (
                    <Text size="xs">{d.orgName ? `${d.orgName} (${d.orgId})` : d.orgId}</Text>
                  ) : (
                    <Text size="xs" c="dimmed">
                      Unknown
                    </Text>
                  )}
                </Table.Td>
              )}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </ScrollArea.Autosize>
  </Paper>
);

interface VersionDistributionViewProps {
  devices: DeviceRow[];
  emptyText: string;
  showOrg: boolean;
}

const VersionDistributionView: React.FC<VersionDistributionViewProps> = ({
  devices,
  emptyText,
  showOrg,
}) => {
  const [selectedVersion, setSelectedVersion] = useState<string | null>(null);
  const chartData = useMemo(() => aggregateByVersion(devices), [devices]);

  // Clear the drill-down when the picked version leaves the data (org/dist changed).
  useEffect(() => {
    if (selectedVersion && !chartData.some((row) => row.version === selectedVersion)) {
      setSelectedVersion(null);
    }
  }, [chartData, selectedVersion]);

  const detailDevices = useMemo(
    () =>
      selectedVersion ? devices.filter((d) => normalizeVersion(d.version) === selectedVersion) : [],
    [selectedVersion, devices],
  );

  if (!chartData.length) {
    return (
      <Text size="sm" c="dimmed" py="md">
        {emptyText}
      </Text>
    );
  }

  const height = Math.max(220, Math.min(600, chartData.length * 32 + 60));

  return (
    <Stack gap="xs">
      <BarChart
        h={height}
        data={chartData}
        dataKey="version"
        orientation="vertical"
        yAxisProps={{ width: 110, interval: 0 }}
        withBarValueLabel
        barChartProps={{
          layout: "vertical",
          style: { cursor: "pointer" },
          onClick: (state: { activeLabel?: string }) => {
            const label = state?.activeLabel;
            if (!label) return;
            setSelectedVersion((prev) => (prev === label ? null : label));
          },
        }}
        series={[{ name: "count", label: "Devices", color: "blue.6" }]}
      />
      <Text size="xs" c="dimmed">
        Click a version bar to list its devices{showOrg ? " and their organizations" : ""}.
      </Text>
      {selectedVersion && (
        <DeviceDetailTable version={selectedVersion} devices={detailDevices} showOrg={showOrg} />
      )}
    </Stack>
  );
};

const ProductVersionDistribution: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string | null>("overall");

  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [distributors, setDistributors] = useState<DistributorLike[]>([]);

  const [overallLoading, setOverallLoading] = useState(false);
  const [overallDevices, setOverallDevices] = useState<DeviceRow[]>([]);
  const [overallLoaded, setOverallLoaded] = useState(false);

  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [orgDevices, setOrgDevices] = useState<DeviceRow[]>([]);
  const [orgLoading, setOrgLoading] = useState(false);

  const [selectedDistId, setSelectedDistId] = useState<string | null>(null);
  const [distDevices, setDistDevices] = useState<DeviceRow[]>([]);
  const [distLoading, setDistLoading] = useState(false);

  // Selectors only; the distribution counts come from the backend endpoint.
  useEffect(() => {
    getDistributorOrgs(
      (res) => setOrgs(Array.isArray(res?.data) ? res.data : []),
      () => setOrgs([]),
    );
    getAllDistributors(
      {},
      (res) => setDistributors(Array.isArray(res?.data) ? res.data : []),
      () => setDistributors([]),
    );
  }, []);

  useEffect(() => {
    if (activeTab !== "overall" || overallLoaded) return;
    let cancelled = false;
    setOverallLoading(true);
    getVersionDistribution(
      {},
      (res) => {
        if (cancelled) return;
        setOverallDevices(toDeviceRows(res?.data));
        setOverallLoaded(true);
        setOverallLoading(false);
      },
      () => {
        if (cancelled) return;
        setOverallDevices([]);
        setOverallLoaded(true);
        setOverallLoading(false);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [activeTab, overallLoaded]);

  useEffect(() => {
    if (!selectedOrgId) {
      setOrgDevices([]);
      setOrgLoading(false);
      return;
    }
    let cancelled = false;
    setOrgLoading(true);
    getVersionDistribution(
      { orgId: selectedOrgId },
      (res) => {
        if (cancelled) return;
        setOrgDevices(toDeviceRows(res?.data));
        setOrgLoading(false);
      },
      () => {
        if (cancelled) return;
        setOrgDevices([]);
        setOrgLoading(false);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [selectedOrgId]);

  useEffect(() => {
    if (!selectedDistId) {
      setDistDevices([]);
      setDistLoading(false);
      return;
    }
    let cancelled = false;
    setDistLoading(true);
    getVersionDistribution(
      { distId: selectedDistId },
      (res) => {
        if (cancelled) return;
        setDistDevices(toDeviceRows(res?.data));
        setDistLoading(false);
      },
      () => {
        if (cancelled) return;
        setDistDevices([]);
        setDistLoading(false);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [selectedDistId]);

  const orgOptions = useMemo(
    () =>
      orgs
        .map((o) => ({
          value: o.orgId,
          label: o.name ? `${o.name} (${o.orgId})` : o.orgId,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [orgs],
  );

  const distOptions = useMemo(
    () =>
      distributors
        .map((d) => ({
          value: d.distId,
          label: d.name ? `${d.name} (${d.distId})` : d.distId,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [distributors],
  );

  return (
    <Paper withBorder radius="md" p="md">
      <Group justify="space-between" align="center" mb="sm">
        <Title order={4}>Product Version Distribution</Title>
        {activeTab === "overall" && overallLoaded && (
          <Text size="sm" c="dimmed">
            {overallDevices.length} device{overallDevices.length === 1 ? "" : "s"} total
          </Text>
        )}
      </Group>
      <Tabs value={activeTab} onChange={setActiveTab} keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="overall">Overall</Tabs.Tab>
          <Tabs.Tab value="byOrg">By Organization</Tabs.Tab>
          <Tabs.Tab value="byDist">By Distributor</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="overall" pt="md">
          {overallLoading ? (
            <Center py="md">
              <Loader />
            </Center>
          ) : (
            <VersionDistributionView
              devices={overallDevices}
              emptyText="No devices found."
              showOrg
            />
          )}
        </Tabs.Panel>

        <Tabs.Panel value="byOrg" pt="md">
          <Stack gap="sm">
            <Select
              label="Organization"
              placeholder="Select an organization"
              data={orgOptions}
              value={selectedOrgId}
              onChange={setSelectedOrgId}
              searchable
              clearable
              nothingFoundMessage="No organizations"
            />
            {orgLoading ? (
              <Center py="md">
                <Loader />
              </Center>
            ) : selectedOrgId ? (
              <VersionDistributionView
                devices={orgDevices}
                emptyText="This organization has no devices with a version reported."
                showOrg={false}
              />
            ) : (
              <Text size="sm" c="dimmed">
                Select an organization to see its device version distribution.
              </Text>
            )}
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="byDist" pt="md">
          <Stack gap="sm">
            <Select
              label="Distributor"
              placeholder="Select a distributor"
              data={distOptions}
              value={selectedDistId}
              onChange={setSelectedDistId}
              searchable
              clearable
              nothingFoundMessage="No distributors"
            />
            {distLoading ? (
              <Center py="md">
                <Loader />
              </Center>
            ) : selectedDistId ? (
              <VersionDistributionView
                devices={distDevices}
                emptyText="No devices found under this distributor's organizations."
                showOrg
              />
            ) : (
              <Text size="sm" c="dimmed">
                Select a distributor to see the device version distribution across all of its
                organizations.
              </Text>
            )}
          </Stack>
        </Tabs.Panel>
      </Tabs>
    </Paper>
  );
};

export default ProductVersionDistribution;
